/**
 * UI-002 — THE DRIVER DOSSIER.
 *
 * Howard, 15 Aug 2026: *"If the user clicks on a racer's card it ought to pop
 * out and surface a lot of info about the driver, the team, the stats, the
 * history, a LOT of information should be surfaced in that modal."*
 *
 * This is the payoff for the telemetry work. Every counter in `TELEMETRY_SPEC`
 * declared a consumer, and most of them said `RECORD` or `STORY` — meaning
 * their only job was to end up on this screen. So nothing here is computed for
 * the display: it is all read off the season fold, the money ledger and the
 * card history, and where a number would be a lie it says so instead.
 *
 * HONEST EMPTY STATES. A career section on a driver in his first season should
 * say "first season" rather than showing zeroes that look like failure. A
 * dossier that pretends to know things is worse than one that admits it does
 * not, because the whole point of this project is that the numbers can be
 * trusted.
 *
 * ===========================================================================
 * THREE SCOPES, ONE CARD — D-EZ-60
 * ===========================================================================
 *
 * Howard, 19 Aug 2026: *"When i open a driver card I want to see current race,
 * then a toggle to season stats, and a 3rd toggle option that shows career
 * stats all within their card without closing it."*
 *
 * THIS RACE · THIS SEASON · CAREER. The panel never closes and never reloads —
 * every scope is folded from what the caller already handed in, and switching
 * is a render, not a fetch.
 *
 * ⚠ THE CAREER SCOPE IS `allTimeBoard`, THE BOOK'S OWN FOLD. Not a second
 * career arithmetic written for a modal. If the number on this card ever
 * disagreed with the same number on The Book, one of them would be wrong and a
 * player would have no way to tell which — so there is exactly one function
 * that adds a driver's seasons up, and both screens call it.
 *
 * ⚠ AND A SCOPE WITH NOTHING BEHIND IT IS DISABLED, NOT EMPTY. Opened from the
 * career page there is no race to show, and a tab that opens onto "no data"
 * teaches a player that the tabs are decorative. The control says which scopes
 * this card can answer, and opens on the first one that has something —
 * preferring the race, which is what Howard asked to land on.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { describeDelta } from '../domain/altCards.ts';
import type { DriverCard, RaceState } from '../domain/types.ts';
import type { DriverSeasonStats, SeasonStandings } from '../domain/standings.ts';
import type { CareerSeason } from '../lib/careerRunner.ts';
import type { Team } from '../domain/field.ts';
import { foldMoney, formatDollars } from '../domain/finance/money.ts';
import { driverMoney, racesInSeason, type RaceMoney } from './driverMoney.ts';
import CarTopDown from './CarTopDown.tsx';
import { logoForTeam, useLogos } from '../lib/logoStore.ts';
import { liveryForDriver } from '../domain/livery.ts';
import { leagueSeasons } from '../domain/story/league.ts';
import TrophyCase from './story/TrophyCase.tsx';
import PeoplePanel from './story/PeoplePanel.tsx';
import { ordinal } from './boardModel.ts';
import { TRAITS_BY_CODE } from '../domain/traits.ts';
import { foldRace, runningOrder, type DriverRaceFold, type RaceFoldSource } from './raceDossier.ts';
import type { GridRow } from './raceWeekModel.ts';
import { allTimeBoard, collectSources } from './statsModel.ts';
import type { LiveSeasonLike, SeasonLine, StatRow } from './statsModel.ts';
import { Cite } from './Cite.tsx';
import { useCiteText } from '../lib/prefs.ts';

/**
 * UI-008 — everything the modal needs to render from a LIVE RACE.
 *
 * A standalone race has no `CareerSeason`, which is why this is a second entry
 * point rather than a second import. `teams` is optional and used for one thing
 * only: the shop's name and which seat in it he holds, so his car is painted
 * the way the boards paint it.
 */
export interface RaceDossierSource extends RaceFoldSource {
  raceName: string;
  teams?: readonly Team[];
  /**
   * Where to find his card when he is not in the grid at this cursor — the
   * starting grid always has him, so this is normally just `start`.
   */
  grid?: RaceState;
}

/**
 * UI-008 — the starting-grid entry point.
 *
 * Howard, 16 Aug 2026: *"on the starting grid as well, i want to be able to
 * click on their card and have the same modal open with all of that rich info."*
 *
 * The grid screen is shown BEFORE `runRace` is called, so there is no tape to
 * fold — the honest source there is the week that set the grid.
 */
export interface WeekDossierSource {
  raceName?: string;
  /** His row on the grid, when he qualified. */
  row?: GridRow;
  /** How he stands with the field: on the grid, sent home, or left on the bubble. */
  standing: 'grid' | 'dnq' | 'bubble';
  /** Every card in the week, for naming a feud partner. */
  cards?: ReadonlyMap<string, DriverCard>;
  teams?: readonly Team[];
  /**
   * The field in GRID ORDER, for stepping ‹ and › through it.
   *
   * Passed in rather than derived, because on this screen the order lives in
   * the week's story (`story.grid`, then `story.dnq`) and the dossier is handed
   * only one driver's slice of it. Omitted, the arrows simply do not appear —
   * which is the honest behaviour for a caller that has no order to offer.
   */
  order?: readonly string[];
}

/**
 * A SEASON IN PROGRESS, as a dossier source — D-EZ-49.
 *
 * Howard, 18 Aug 2026: *"In The Book, if I click on a racer name it ought to
 * open their driver card modal like the other locations."*
 *
 * It did not, and the reason was a single line: `if (!card) return null`. The
 * card is found from the `CareerSeason`, and while a season is being raced there
 * is no `CareerSeason` — so the modal opened onto nothing and looked to a player
 * exactly like a dead link.
 *
 * ⚠ THIS IS NOT A FAKE `week`. A `WeekDossierSource` would have supplied the
 * card in one line, and it would also have put "on the grid" on the screen for a
 * driver who is not on any grid right now. A source that lies to make a
 * component render is worse than the component not rendering.
 *
 * What it carries is exactly what a live season HAS: the licensed cards, the
 * garage, and the championship as it stands this week. The sections that need a
 * winter or a settled set of books still say so plainly — they say it in
 * season-aware words now instead of telling a player in the middle of a
 * championship that he is running a one-off race.
 */
export interface SeasonDossierSource {
  seasonNumber: number;
  /** Every licensed card, so a driver — and a feud partner — can be named. */
  cards: ReadonlyMap<string, DriverCard>;
  teams?: readonly Team[];
  /** The fold as it stood after the most recent race. */
  standings?: SeasonStandings;
  /** Races run so far. 0 before the first flag. */
  throughRace?: number;
  totalRaces?: number;
  /** Championship order, so ‹ and › step down the standings. */
  order?: readonly string[];
}

/** D-EZ-60 — the three things a card can be asked about. */
export type DossierScope = 'race' | 'season' | 'career';

const SCOPES: readonly { key: DossierScope; label: string; empty: string }[] = [
  {
    key: 'race',
    label: 'This race',
    empty: 'This card was not opened from a race, so there is no live tape behind it.',
  },
  {
    key: 'season',
    label: 'This season',
    empty: 'A one-off race has no championship behind it, so there are no season numbers.',
  },
  {
    key: 'career',
    label: 'Career',
    empty: 'No settled season has been played yet, so there is no career to add up.',
  },
];

export interface DriverDossierProps {
  driverId: string;
  /**
   * The season this driver's numbers come from. OPTIONAL since UI-008: a
   * one-off race has no season, and the season, money and winter sections say
   * so plainly rather than showing zeroes that read as failure.
   */
  career?: CareerSeason;
  /** Every season played so far, oldest first. Used for the career columns. */
  history?: CareerSeason[];
  /** The live race, when the dossier was opened from the boards. */
  race?: RaceDossierSource;
  /** The week, when the dossier was opened from the starting grid. */
  week?: WeekDossierSource;
  /** A season being raced right now. See `SeasonDossierSource`. */
  season?: SeasonDossierSource;
  /**
   * D-EZ-60 — the live season itself, so the CAREER scope can count a year that
   * has not been settled yet. Without it a driver's career total silently stops
   * at the last winter, which is the sort of quiet wrongness this project is
   * built to avoid.
   */
  liveSeason?: LiveSeasonLike | null;
  onClose: () => void;
  onSelectDriver?: (driverId: string) => void;
}

export default function DriverDossier({
  driverId,
  career,
  history = [],
  race,
  week,
  season,
  liveSeason,
  onClose,
  onSelectDriver,
}: DriverDossierProps) {
  const panel = useRef<HTMLDivElement>(null);
  /**
   * The stepper, behind a ref.
   *
   * The key handler is bound once per driver; `step` is rebuilt whenever the
   * field or the cursor moves. Binding the handler to `step` directly would
   * add and remove a window listener on every one of those, and — worse —
   * `step` is defined below this effect, so the effect would be reading it
   * before it exists. A ref is read at the moment the key is pressed, which is
   * the only moment its value matters.
   */
  const stepRef = useRef<(by: number) => void>(() => {});

  // Escape closes; focus moves into the panel so a keyboard can read it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      // The modal holds no text input and no other arrow-key control, so the
      // arrows are free to mean the one thing there is to mean here.
      if (e.key === 'ArrowLeft') stepRef.current(-1);
      if (e.key === 'ArrowRight') stepRef.current(1);
    };
    window.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, driverId]);

  const data = useMemo(
    () => (career ? gather(driverId, career, history) : null),
    [driverId, career, history],
  );

  /*
    ═══════════════════════════════════════════════════════════════════════════
    THE MONEY, ACROSS EVERYTHING HE HAS RACED — D-EZ-82.

    ⚠ `gather()` READS ONE SEASON'S LEDGER AND THIS DOES NOT. That is the whole
    reason this is a second fold rather than another field on `Gathered`:
    `career` is ONE settled season, and "career winnings" taken from it was
    short by 57% over two seasons and got worse the longer a career ran. It read
    low and it read plausible, which is the worst pair of properties a number
    can have.

    Everything settled, oldest first, PLUS the season being played — because
    Howard's own league was mid-season-2 at race 7 and the card was telling him
    about a settled season 1 and nothing about the seven races he had just run.

    `driverMoney` is pure and tested on its own (`driverMoney.test.ts`), against
    the engine's own per-season ledgers.
    ═══════════════════════════════════════════════════════════════════════════
  */
  const purse = useMemo(() => {
    const settled = career ? [...history, career] : history;
    const liveEvents = liveSeason?.state.finance?.events ?? null;
    const schedule = liveSeason?.state.schedule.races ?? [];
    return driverMoney(
      driverId,
      settled,
      liveEvents && liveSeason
        ? {
            seasonNumber: liveSeason.seasonNumber,
            events: liveEvents,
            raceName: (n) => schedule.find((r) => r.raceNumber === n)?.raceName ?? null,
          }
        : null,
    );
  }, [driverId, career, history, liveSeason]);

  /**
   * THE LIVE FOLD, RECOMPUTED ON THE CURSOR.
   *
   * `race.cursor` is in the dependency list, which is what makes the modal obey
   * the tape: step the boards back to turn 12 with the dossier open and the fold
   * re-runs over twelve turns. `foldRace` slices the tape, so it cannot see
   * turn 40 whatever this component does.
   */
  const live = useMemo(() => (race ? foldRace(race, driverId) : null), [race, driverId]);

  /**
   * D-EZ-60 — THE CAREER, THROUGH THE BOOK'S OWN DOOR.
   *
   * `collectSources` + `allTimeBoard` is exactly what the CAREER page's
   * all-time table is built from, so a driver's 41 starts here is the same 41
   * starts there by construction rather than by coincidence. It also handles
   * the awkward case for free: a live season is dropped when a settled
   * `CareerSeason` already exists for the same year, so the winter landing does
   * not briefly double his numbers.
   */
  const sources = useMemo(
    () => collectSources({ history, career, live: liveSeason }),
    [history, career, liveSeason],
  );

  const board = useMemo(() => (sources.length > 0 ? allTimeBoard(sources) : null), [sources]);
  /** D-EZ-102 — the league's seasons, for the trophy case. */
  const leagueSeasonsHere = useMemo(
    () => leagueSeasons({ history, career: career ?? null, live: liveSeason ?? null }),
    [history, career, liveSeason],
  );

  /**
   * ⚠ HIS CARD, FROM THE SEASONS, WHEN NOTHING ELSE HAS IT.
   *
   * Found by a screenshot: a driver who is in the history but not in the season
   * on the workbench — a rookie signed two winters ago, anyone from a league
   * whose current season has not been dealt — had no card anywhere the lookup
   * looked, so the modal returned null and opened onto a black rectangle. The
   * career fold knows perfectly well who he is; it just was not asked.
   *
   * Newest season first, because the card he raced most recently is the one a
   * player means by "his card".
   */
  const careerCard = useMemo(() => {
    for (let i = sources.length - 1; i >= 0; i -= 1) {
      const found = sources[i].roster.find((c) => c.driverId === driverId);
      if (found) return found;
    }
    return undefined;
  }, [sources, driverId]);

  const careerRow = useMemo<StatRow | null>(
    () => board?.drivers.find((d) => d.driverId === driverId) ?? null,
    [board, driverId],
  );
  const careerLines = useMemo<readonly SeasonLine[]>(
    () => board?.perSeason[driverId] ?? [],
    [board, driverId],
  );

  const raceCard = useMemo(() => (race ? cardInRace(race, driverId) : undefined), [race, driverId]);
  const weekCard = week?.row?.card ?? week?.cards?.get(driverId);

  const card = data?.card ?? raceCard ?? weekCard ?? season?.cards.get(driverId) ?? careerCard;

  /*
    ⚠ THE EARLY RETURN MOVED BELOW THE HOOKS, and that is a fix, not a tidy-up.
    It used to sit here, above `raceSeat`, `siblings` and `step` — so a driver
    whose card could not be found returned before those ran and changed the hook
    count between renders. Nothing hit it because the modal is opened with a
    driver who exists, but stepping ‹ › onto a card the caller had not supplied
    would have thrown inside React rather than showing nothing.
  */

  // The live fold, when there is no settled one. Same type, same fold, one race
  // earlier — which is the whole reason the season section below works
  // unchanged rather than needing a second copy written for mid-season.
  const stat = data?.stat ?? season?.standings?.byDriver[driverId];
  const team = data?.team;
  /*
    ONE SEASON IN THE MONEY PANEL (audit, 4 Oct 2026). Mid-season the panel's
    totals came from the SETTLED season's ledger while its weekend list came
    from the season being raced — last year's salary over this year's Sundays.
    When a season is being raced past the settled one, its own weekends are the
    season, so the totals are folded from them too.
  */
  const liveMoney = useMemo(() => {
    if (!liveSeason?.state.finance) return null;
    if (career && liveSeason.seasonNumber <= career.seasonNumber) return null;
    const fin = liveSeason.state.finance;
    const ledger = foldMoney(fin.events);
    const book = ledger.books[`driver:${driverId}`];
    const teamId = fin.teamOf[driverId];
    const teamBook = teamId ? ledger.books[`team:${teamId}`] : undefined;
    return {
      careerWinningsMb: book?.careerWinningsMb ?? 0,
      salary: book?.byCategory.salary ?? 0,
      royalties: book?.byCategory.royalties ?? 0,
      bonus: book?.byCategory.bonus ?? 0,
      fines: -(book?.byCategory.fine ?? 0),
      balanceMb: book?.balanceMb ?? 0,
      teamBalanceMb: teamBook?.balanceMb ?? 0,
      teamFailed: null,
    };
  }, [liveSeason, career, driverId]);
  const money = liveMoney ?? data?.money;
  const seasons = data?.seasons ?? [];
  const rerate = data?.rerate;
  const weeks = data?.weeks ?? [];
  const feuds = data?.feuds ?? [];
  const teammates = data?.teammates ?? [];
  const teammateIndex = data?.teammateIndex ?? -1;

  // `seasons` includes next year's card once the winter has run, so it is one
  // ahead of what he has actually raced.
  const seasonsRaced = career ? seasons.filter((s) => s.season <= career.seasonNumber).length : 0;
  void seasonsRaced;

  /*
    WHICH SEASON THE "THIS SEASON" SCOPE IS ABOUT — D-EZ-82.

    ⚠ THE LIVE SEASON WINS, and this is the same mistake in a second place. The
    settled `career` is LAST year once a new one has started, so a card opened
    at race 7 of season 2 was scoped to season 1 without saying so. The season
    being raced is the season a player means by "this season".
  */
  const shownSeason = liveSeason?.seasonNumber ?? career?.seasonNumber ?? 0;

  // Off the boards or the grid, the shop comes from the garage the field was
  // built with.
  const raceSeat = useMemo(() => {
    const garage = race?.teams ?? week?.teams ?? season?.teams;
    if (!garage) return undefined;
    for (const t of garage) {
      const i = t.driverIds.indexOf(driverId);
      if (i >= 0) return { team: t, index: i };
    }
    return undefined;
  }, [race, week, season, driverId]);

  const shopName = team?.name ?? raceSeat?.team.name;
  const inkTeamId = team?.teamId ?? raceSeat?.team.teamId;
  const inkBase = team?.baseInk ?? raceSeat?.team.baseInk;
  const seatIndex = team ? Math.max(0, teammateIndex) : raceSeat?.index;
  // The shop's mark on the hood of the car in the dossier header, by name.
  const hoodLogo = logoForTeam(useLogos(), shopName ?? null);

  /**
   * ‹ AND › — STEPPING THROUGH THE FIELD WITHOUT CLOSING THE CARD.
   *
   * Howard, 17 Aug 2026: *"can you add a left arrow and right arrow so they can
   * increment up and down the driver cards without having to close them and
   * reopen another one without leaving the card modal entirely?"*
   *
   * The order is DERIVED where it can be and PASSED where it cannot:
   *
   *   from the boards — `runningOrder(race)` reads the running order at the
   *     CURSOR, so ‹ › walks the field in the order the screen behind the modal
   *     is showing it. Deriving it here rather than taking it as a prop means
   *     the two can never disagree, which they would the first time a caller
   *     forgot to update it after a rewind.
   *   from the grid — the week's story owns the order and hands it in, because
   *     the dossier is given only one driver's slice of it.
   *
   * EMPTY IS A REAL ANSWER. The career screen opens this with neither source,
   * and there the arrows do not appear at all rather than appearing dead.
   */
  const siblings = useMemo<readonly string[]>(
    () =>
      week?.order
        ? week.order
        : race
          ? runningOrder(race)
          : (season?.order ?? []),
    [week?.order, race, season?.order],
  );
  const at = siblings.indexOf(driverId);
  const canStep = at >= 0 && siblings.length > 1 && Boolean(onSelectDriver);
  const step = useCallback(
    (by: number) => {
      if (!canStep) return;
      const next = siblings[at + by];
      // No wrapping. At 1st, ‹ is spent; at last, › is. Wrapping would make
      // "am I at the end of the field" unanswerable without counting, and the
      // disabled button says it for free.
      if (next) onSelectDriver?.(next);
    },
    [canStep, siblings, at, onSelectDriver],
  );
  stepRef.current = step;

  /**
   * D-EZ-60 — WHICH SCOPES THIS CARD CAN ACTUALLY ANSWER.
   *
   * Availability is a fact about the caller, not a preference: the boards hand
   * in a race, the career page hands in seasons, a one-off exhibition hands in
   * neither a season nor a career. A tab is offered only where there is
   * something behind it.
   */
  const canShow = useMemo(
    () => ({
      race: Boolean(race || week),
      season: Boolean(career || season),
      career: Boolean(careerRow),
    }),
    [race, week, career, season, careerRow],
  );

  /*
    ⚠ DERIVED AT MOUNT, NOT CORRECTED AFTERWARDS — and a screenshot is why.

    This started as `useState('race')` with an effect that moved off an empty
    scope. Opened from the career page, where there is no race, that renders one
    frame of a blank panel under a disabled tab before the effect fires. The
    first paint has to be right: the opening scope is Howard's order — the race,
    then the season, then the career — resolved from the props that are already
    in hand.

    The effect below stays, but only for what it is actually for: availability
    CHANGING under a panel that is already open, which is what stepping ‹ › on
    to a driver with a different record does.
  */
  const [scope, setScope] = useState<DossierScope>(() =>
    race || week ? 'race' : career || season ? 'season' : 'career',
  );
  useEffect(() => {
    setScope((current) =>
      canShow[current] ? current : (SCOPES.find((s) => canShow[s.key])?.key ?? 'race'),
    );
  }, [canShow]);

  if (!card) return null;

  return (
    <div className="dossier__scrim" role="dialog" aria-modal="true" aria-label={`${card.name} dossier`} onClick={onClose}>
      <div className="dossier" ref={panel} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        {/*
          THE STEPPERS AND THE CLOSE, in one row at the top of the panel.

          ‹ and › are DISABLED rather than hidden at the two ends of the field:
          a control that vanishes when you reach 1st makes the panel jump and
          leaves the player wondering whether they broke something. Disabled,
          with the count beside it, says "you are at the front" without a word.
        */}
        <div className="dossier__nav">
          {canStep && (
            <>
              <button
                type="button"
                className="dossier__step"
                onClick={() => step(-1)}
                disabled={at <= 0}
                aria-label="Previous driver"
                title="Previous driver (←)"
              >
                ‹
              </button>
              <span className="dossier__navcount" aria-hidden>
                {at + 1} of {siblings.length}
              </span>
              <button
                type="button"
                className="dossier__step"
                onClick={() => step(1)}
                disabled={at >= siblings.length - 1}
                aria-label="Next driver"
                title="Next driver (→)"
              >
                ›
              </button>
            </>
          )}
          <button type="button" className="dossier__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {/* ---- who he is ------------------------------------------------ */}
        <header className="dossier__head">
          <div className="dossier__car">
            <CarTopDown
              livery={liveryForDriver(card.driverId, inkTeamId, seatIndex, inkBase as never, card.make)}
              carNumber={card.carNumber}
              size={128}
              hoodLogoUrl={hoodLogo?.url ?? null}
              hoodLogoLabel={hoodLogo?.label ?? null}
            />
          </div>
          <div className="dossier__id">
            <div className="dossier__kicker">
              {shopName ?? 'Independent'}
              {stat && <> · {ordinal(stat.rank)} in points</>}
              {live && live.position !== null && <> · running {ordinal(live.position)}</>}
              {live && live.status === 'out' && <> · out of the race</>}
            </div>
            <h2>{card.name}</h2>
            <div className="dossier__strip">
              <Chip label="Gas" value={card.gasPedal} />
              <Chip label="Brake" value={card.brakePedal} />
              <Chip label="Escape" value={card.escape} />
              <Chip label="Fast pit" value={card.fastPit} />
              <Chip label="Crew" value={card.pitCrewGrade} />
              <Chip label="Trophy" value={card.trophy ? card.trophy.toUpperCase() : 'NONE'} tone={card.trophy ?? undefined} />
            </div>
            <div className="dossier__qual">
              {card.qualities.map((q) => (
                <span key={q} className={card.starred.includes(q) ? 'q is-starred' : 'q'}>
                  {q}
                  {card.starred.includes(q) && <b>★</b>}
                </span>
              ))}
              {card.triangle && <span className="q q--tri">▲ blows up on a red 6</span>}
              {card.dnfExempt && (
                <span className="q q--ok" title="2025 set — only a failed REPAIR is forgiven; 13, 21 and 66 still put him out">
                  ▲ a failed repair does not end his day
                </span>
              )}
              {/*
                D-EZ-76 — the second card, said as what it IS: a difference from
                the card printed above it. "Road Courses: +2 gas, +3 brake, 2
                crew grades better" is a sentence a player can act on; a second
                set of numbers in a corner is a puzzle.
              */}
              {card.altCard && (
                <span className="q q--alt" title={`He races off a different card at these tracks — ${card.altCard.label}`}>
                  {card.altCard.label}: {describeDelta(card.altCard.delta) || 'no change'}
                </span>
              )}
              {card.trait && (
                <span className="q q--trait" title={TRAITS_BY_CODE[card.trait].text}>
                  [{card.trait}] {TRAITS_BY_CODE[card.trait].name}
                </span>
              )}
            </div>
          </div>
          {money ? (
            <div className="dossier__money">
              {/* D-EZ-82 — every season he has money in, the one being played
                  included. NOT `money.careerWinningsMb`, which is one year. */}
              <div className="dossier__money-v">{formatDollars(purse.careerWinningsMb)}</div>
              <div className="dossier__money-l">Career winnings</div>
              <div className="dossier__money-n">
                {purse.seasonsCounted} season{purse.seasonsCounted === 1 ? '' : 's'} · money won racing, not money paid
              </div>
            </div>
          ) : live ? (
            <div className="dossier__money">
              <div className="dossier__money-v">
                {live.position !== null ? ordinal(live.position) : '—'}
              </div>
              <div className="dossier__money-l">
                {live.status === 'out' ? 'Out of the race' : 'Running'}
              </div>
              <div className="dossier__money-n">
                {live.cursor === 0
                  ? 'on the grid, before the green'
                  : `after turn ${live.turnShowing - 1 > 0 ? live.turnShowing - 1 : live.cursor}`}
              </div>
            </div>
          ) : week ? (
            <div className="dossier__money">
              <div className="dossier__money-v">
                {week.standing === 'grid' && week.row ? ordinal(week.row.position) : '—'}
              </div>
              <div className="dossier__money-l">
                {week.standing === 'grid' ? 'Starts' : week.standing === 'dnq' ? 'Did not qualify' : 'On the bubble'}
              </div>
              <div className="dossier__money-n">before the green</div>
            </div>
          ) : null}
        </header>

        {/* D-EZ-102 — who he is beyond the card: age, hometown, titles, awards,
            the Hall, sponsors with tenure, career-best season, milestones,
            rivalries, and how a finished career ended. */}
        <TrophyCase
          driverId={card.driverId}
          seasons={leagueSeasonsHere}
          contracts={liveSeason?.state.finance?.contracts ?? career?.finance.state ?? null}
          age={card.age ?? null}
        />

        {/* D-EZ-107 / D-EZ-108 — who he is, and who he has history with. */}
        {leagueSeasonsHere.length > 0 && (
          <PeoplePanel driverId={card.driverId} seasons={leagueSeasonsHere} onSelectDriver={onSelectDriver} />
        )}

        {/*
          D-EZ-60 — THE THREE SCOPES.

          Chips rather than a segmented control, and the same `.sb__chip` The
          Book uses, because this is the same question that screen asks: which
          slice of a driver's record am I looking at. A scope with nothing
          behind it is disabled and says why on hover — see SCOPES.
        */}
        <nav className="dossier__scopes" aria-label="What to show">
          {SCOPES.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`sb__chip${scope === s.key ? ' is-on' : ''}`}
              onClick={() => setScope(s.key)}
              aria-pressed={scope === s.key}
              disabled={!canShow[s.key]}
              title={canShow[s.key] ? undefined : s.empty}
            >
              {s.label}
            </button>
          ))}
          <span className="dossier__scopenote">
            {scope === 'race'
              ? (race?.raceName ?? week?.raceName ?? 'the race in front of him')
              : scope === 'season'
                ? career && !liveMoney
                  ? `Season ${career.seasonNumber}, settled`
                  : season
                    ? `Season ${season.seasonNumber}${season.throughRace ? `, through race ${season.throughRace}` : ', not started'}`
                    : ''
                : careerRow
                  ? `${careerRow.seasons} season${careerRow.seasons === 1 ? '' : 's'}, ${careerRow.starts} start${careerRow.starts === 1 ? '' : 's'}`
                  : ''}
          </span>
        </nav>

        <div className="dossier__grid">
          {/* ---- UI-008 · THIS RACE, LIVE -------------------------------- */}
          {scope === 'race' && live && <LiveRace fold={live} raceName={race?.raceName ?? 'this race'} />}

          {/* ---- UI-008 · BEFORE THE GREEN ------------------------------- */}
          {scope === 'race' && !live && week && (
            <PreRace
              row={week.row}
              standing={week.standing}
              raceName={week.raceName}
              nameOf={(id) => week.cards?.get(id)?.name ?? id}
              onSelectDriver={onSelectDriver}
            />
          )}

          {/* ---- this season -------------------------------------------- */}
          {scope === 'season' && (
            <>
          <section className="dbox">
            <h3>
              {career
                ? `Season ${career.seasonNumber}`
                : season
                  ? `Season ${season.seasonNumber}`
                  : 'This season'}
              {!career && season && (
                <span className="dossier__live">
                  {season.throughRace
                    ? ` — through race ${season.throughRace}${season.totalRaces ? ` of ${season.totalRaces}` : ''}`
                    : ' — not started'}
                </span>
              )}
            </h3>
            {!career && !season ? (
              <p className="dim">
                A one-off race — there is no championship behind it, so there are no season numbers to
                show. Run him in a career season and this fills in.
              </p>
            ) : stat ? (
              <dl className="stats">
                <Stat k="Points" v={stat.points} />
                <Stat k="Starts" v={stat.starts} />
                <Stat k="Wins" v={stat.wins} hot={stat.wins > 0} />
                <Stat k="Poles" v={stat.poles} />
                <Stat k="Top 5" v={stat.top5} />
                <Stat k="Top 10" v={stat.top10} />
                <Stat k="Turns led" v={stat.turnsLed} />
                <Stat k="Races led" v={stat.racesLed} />
                <Stat k="Avg start" v={stat.avgStart?.toFixed(1) ?? '—'} />
                <Stat k="Avg finish" v={stat.avgFinish?.toFixed(1) ?? '—'} />
                <Stat k="Best" v={stat.bestFinish ? ordinal(stat.bestFinish) : '—'} />
                <Stat k="DNFs" v={stat.dnfs} />
                <Stat k="DNQs" v={stat.dnqs} />
                <Stat k="Missed the show" v={stat.missedTheShow} />
              </dl>
            ) : (
              <p className="dim">He did not start a race this season.</p>
            )}
          </section>

          {/* ---- how he drove ------------------------------------------- */}
          <section className="dbox">
            <h3>How he drove{career || season ? '' : ', across the season'}</h3>
            {!career && !season ? (
              <p className="dim">
                Season-long form needs a season. Everything this race knows about how he is driving
                is in <strong>This race</strong>, above.
              </p>
            ) : stat ? (
              <>
                <dl className="stats">
                  <Stat k="Trouble faced" v={stat.troubleAttempts} />
                  <Stat k="Escaped" v={stat.troubleEscapes} />
                  <Stat k="Collected" v={stat.troubleCollected} />
                  <Stat k="Bought out with a chip" v={stat.troubleChipSaves} />
                  <Stat k="Escape rate" v={stat.escapePct === null ? '—' : `${Math.round(stat.escapePct * 100)}%`} />
                  <Stat k="Races finished" v={stat.racesFinished} />
                  <Stat k="RAF%" v={stat.rafPct === null ? '—' : `${Math.round(stat.rafPct * 100)}%`} />
                  <Stat k="Duels started" v={stat.duelsInstigated} />
                  <Stat k="Duels won" v={stat.duelsWon} />
                  <Stat k="Duels lost" v={stat.duelsLost} />
                  <Stat k="Trouble started" v={stat.heatIncidents} note="the HEAT input" />
                  <Stat k="Races in a feud" v={stat.feudRaces} />
                </dl>
                {Object.keys(stat.dnfsByCause).length > 0 && (
                  <p className="dim small">
                    Out by:{' '}
                    {Object.entries(stat.dnfsByCause)
                      .map(([cause, n]) => `${cause} ×${n}`)
                      .join(' · ')}
                  </p>
                )}
              </>
            ) : (
              <p className="dim">Nothing to report.</p>
            )}
          </section>

          {/* ---- the money ---------------------------------------------- */}
          <section className="dbox">
            <h3>The money</h3>
            {money ? (
              <>
                <dl className="stats stats--money">
                  <Stat k="Won racing" v={formatDollars(money.careerWinningsMb)} good />
                  <Stat k="Salary" v={formatDollars(money.salary)} />
                  <Stat k="Royalties" v={formatDollars(money.royalties)} />
                  <Stat k="Bonus" v={formatDollars(money.bonus)} />
                  <Stat k="Fines" v={money.fines > 0 ? `−${formatDollars(money.fines)}` : '—'} bad={money.fines > 0} />
                  <Stat k="Total earned" v={formatDollars(money.balanceMb)} />
                </dl>
                <p className="dim small">
                  Winnings are {money.balanceMb > 0 ? Math.round((money.careerWinningsMb / money.balanceMb) * 100) : 0}% of what
                  he took home. Sagliano's whole argument for the p.31 salary ladder is that this number should be large
                  enough for racing to matter.
                </p>
                {/*
                  D-EZ-82 — WEEKEND BY WEEKEND. Howard: *"on a driver's card, the
                  finances dont track per week? Shouldnt they? Can they?"* They
                  always did — `foldMoney` has kept `byRace` since the ledger was
                  written and nothing ever read it. A season total tells you he
                  had a good year; this tells you which Sundays paid for it.
                */}
                {/* This panel only renders under the season scope, so the list
                    is this season's. The career scope has its own season-by-
                    season table a few sections down. */}
                <Weekends races={racesInSeason(purse, shownSeason)} showSeason={false} />
              </>
            ) : (
              <p className="dim">
                Nobody keeps books on an exhibition. This race pays a purse — it is on the{' '}
                <strong>Payday</strong> panel under the results — but a career winnings figure means a
                career, and there is not one behind this race.
              </p>
            )}
          </section>

          {/* ---- the team ----------------------------------------------- */}
          <section className="dbox">
            <h3>The team</h3>
            {team && career && money ? (
              <>
                <p className="dossier__team">{team.name}</p>
                <dl className="stats">
                  <Stat k="Crew grade" v={career.offseason.crewGrades[team.teamId] ?? card.pitCrewGrade} />
                  <Stat k="Season P&L" v={formatDollars(money.teamBalanceMb)} good={money.teamBalanceMb > 0} bad={money.teamBalanceMb < 0} />
                </dl>
                {teammates.length > 0 && (
                  <>
                    <h4>Teammates</h4>
                    <ul className="dlist">
                      {teammates.map((t) => (
                        <li key={t.driverId}>
                          <button type="button" className="linkish" onClick={() => onSelectDriver?.(t.driverId)}>
                            {t.name}
                          </button>
                          <span className="dim"> — {t.rank ? `${ordinal(t.rank)} in points` : 'unranked'}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {money.teamFailed && (
                  <p className="warn small">{money.teamFailed}</p>
                )}
              </>
            ) : shopName ? (
              <>
                <p className="dossier__team">{shopName}</p>
                <dl className="stats">
                  <Stat k="Crew grade" v={card.pitCrewGrade} />
                  <Stat k="Trophy" v={card.trophy ? card.trophy.toUpperCase() : 'none'} />
                </dl>
                <p className="dim small">
                  The shop's books are a season-long thing. This is the crew he brought to the track.
                </p>
              </>
            ) : (
              <p className="dim">He runs independent — his own charter, his own books.</p>
            )}
          </section>

          {/* ---- his race weeks ----------------------------------------- */}
          <section className="dbox dbox--wide">
            <h3>His race weeks</h3>
            {!career ? (
              <p className="dim">
                Race-week television is a season's worth of readings. This week's is on the{' '}
                <strong>Home</strong> screen.
              </p>
            ) : weeks.length === 0 ? (
              <p className="dim">Television never came near him all year.</p>
            ) : (
              <ul className="dweeks">
                {weeks.slice(0, 12).map((w, i) => (
                  <li key={i}>
                    <span className="dweeks__r">R{w.race}</span>
                    <span className="dweeks__t">{w.text}</span>
                    <Cite className="cite">{w.chart} {w.code}</Cite>
                  </li>
                ))}
              </ul>
            )}
            {weeks.length > 12 && <p className="dim small">…and {weeks.length - 12} more.</p>}
          </section>

          {/* ---- who he fell out with ----------------------------------- */}
          {feuds.length > 0 && (
            <section className="dbox dbox--wide">
              <h3>Who he fell out with</h3>
              <ul className="dlist">
                {feuds.map((f, i) => (
                  <li key={i}>
                    <button type="button" className="linkish" onClick={() => onSelectDriver?.(f.otherId)}>
                      {f.other}
                    </button>
                    <span className="dim">
                      {' '}— live for {f.races} race{f.races === 1 ? '' : 's'}, from R{f.from}
                      {f.endedBy ? `, ${f.endedBy.replace('-', ' ')}` : ', still running at the flag'}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
            </>
          )}

          {scope === 'career' && (
            <>
          {/* ---- everything he has ever done ---------------------------- */}
          <section className="dbox dbox--wide">
            <h3>
              Career
              {careerRow && (
                <span className="dossier__live">
                  {' '}— {careerRow.seasons} season{careerRow.seasons === 1 ? '' : 's'}
                  {careerRow.championships > 0
                    ? `, ${careerRow.championships} championship${careerRow.championships === 1 ? '' : 's'}`
                    : ''}
                </span>
              )}
            </h3>
            {careerRow ? (
              <>
                <h4>What he has done</h4>
                <dl className="stats">
                  <Stat k="Starts" v={careerRow.starts} />
                  <Stat k="Wins" v={careerRow.wins} hot={careerRow.wins > 0} />
                  <Stat k="Poles" v={careerRow.poles} />
                  <Stat k="Top 5" v={careerRow.top5} />
                  <Stat k="Top 10" v={careerRow.top10} />
                  <Stat k="Points" v={careerRow.points} />
                  <Stat k="Best" v={careerRow.bestFinish ? ordinal(careerRow.bestFinish) : '—'} />
                  <Stat k="Avg finish" v={careerRow.avgFinish?.toFixed(1) ?? '—'} />
                  <Stat k="Turns led" v={careerRow.turnsLed} />
                  <Stat k="Races led" v={careerRow.racesLed} />
                  <Stat k="DNFs" v={careerRow.dnfs} />
                  <Stat k="DNQs" v={careerRow.dnqs} />
                </dl>
                {/*
                  RATES, KEPT APART FROM COUNTS. Nine wins is a fact about a
                  career's length as much as its quality; nine wins in forty
                  starts is the other thing, and putting the two in one list
                  invites reading a long career as a good one.
                */}
                <h4>Rates, per start</h4>
                <dl className="stats">
                  <Stat k="Win rate" v={pct(careerRow.winPct)} />
                  <Stat k="Top 5 rate" v={pct(careerRow.top5Pct)} />
                  <Stat k="Top 10 rate" v={pct(careerRow.top10Pct)} />
                  <Stat k="Finished" v={pct(careerRow.finishPct)} />
                  <Stat k="Led a race" v={pct(careerRow.ledPct)} />
                  <Stat k="Duels won" v={pct(careerRow.duelWinPct)} />
                  <Stat
                    k="Turns led / start"
                    v={careerRow.turnsLedPerStart?.toFixed(1) ?? '—'}
                  />
                  <Stat k="Won racing" v={formatDollars(careerRow.winningsMb)} good />
                </dl>
                {careerLines.length > 0 && <h4>Season by season</h4>}
                {careerLines.length > 0 && (
                  <table className="dtable">
                    <thead>
                      <tr>
                        <th>Season</th>
                        <th className="num">Champ.</th>
                        <th className="num">Points</th>
                        <th className="num">Starts</th>
                        <th className="num">Wins</th>
                        <th className="num">Top 5</th>
                        <th className="num">Top 10</th>
                        <th className="num">Avg fin.</th>
                        <th className="num">Won</th>
                      </tr>
                    </thead>
                    <tbody>
                      {careerLines.map((line) => (
                        <tr key={line.seasonNumber}>
                          <td className="nm">
                            {line.seasonNumber}
                            {line.champion && <span className="dossier__champ" title="Champion"> ★</span>}
                          </td>
                          <td className="num">{ordinal(line.rank)}</td>
                          <td className="num">{line.points}</td>
                          <td className="num">{line.starts}</td>
                          <td className={`num${line.wins > 0 ? ' hot' : ''}`}>{line.wins}</td>
                          <td className="num">{line.top5}</td>
                          <td className="num">{line.top10}</td>
                          <td className="num">{line.avgFinish?.toFixed(1) ?? '—'}</td>
                          <td className="num">{formatDollars(line.winningsMb)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {/*
                      ⚠ THE LINE ACROSS THE BOTTOM — D-EZ-64.

                      Howard, 19 Aug 2026: *"like the stat back of a baseball
                      card, each season should have a row and then a total at the
                      bottom."* He is right, and it is the oldest convention in
                      sports statistics for a reason: a column of seasons is only
                      half a record until something adds it up where the eye
                      already is.

                      ⚠ EVERY FIGURE HERE IS `careerRow`, NOT A SUM OF THE ROWS
                      ABOVE. Adding the column up in the view would be a second
                      arithmetic on the same question, and the two would drift
                      the first time a season landed in the fold that is not on
                      this table. Championship position is blank on purpose: a
                      career does not HAVE a finishing position, and printing one
                      would be inventing a fact — the best of them is in "Best"
                      above. The average is the career average, which is not the
                      average of the averages.
                    */}
                    <tfoot>
                      <tr className="dtable__total">
                        <td className="nm">
                          Career
                          <span className="dim"> · {careerLines.length}</span>
                        </td>
                        <td className="num dim">—</td>
                        <td className="num">{careerRow.points}</td>
                        <td className="num">{careerRow.starts}</td>
                        <td className={`num${careerRow.wins > 0 ? ' hot' : ''}`}>{careerRow.wins}</td>
                        <td className="num">{careerRow.top5}</td>
                        <td className="num">{careerRow.top10}</td>
                        <td className="num">{careerRow.avgFinish?.toFixed(1) ?? '—'}</td>
                        <td className="num">{formatDollars(careerRow.winningsMb)}</td>
                      </tr>
                    </tfoot>
                  </table>
                )}
                <p className="dim small">
                  Added up by the same fold the CAREER page's all-time table uses, so these are the
                  numbers on that screen and not a second opinion about them. The bottom line is that
                  fold, not the column above it added up — a championship position is a season's
                  fact and a career does not have one.
                </p>
              </>
            ) : (
              <p className="dim">
                No season has been played to a finish yet, so there is no career behind him.
              </p>
            )}
          </section>

          {/* ---- the winter --------------------------------------------- */}
          <section className="dbox dbox--wide">
            <h3>What the winter did to his card</h3>
            {!career ? (
              <p className="dim">
                {season
                  ? 'The winter has not happened yet. His card comes through the re-rate when the season is settled.'
                  : 'Re-rates happen between seasons. Outside a career there is no winter for his card to come through.'}
              </p>
            ) : rerate ? (
              <>
                <p className="dossier__story">{rerate.story}</p>
                <table className="dtable">
                  <thead>
                    <tr>
                      <th>Rating</th>
                      <th>Was</th>
                      <th>Now</th>
                      <th>Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rerate.steps
                      .filter((s) => s.changed)
                      .map((s) => (
                        <tr key={s.field}>
                          <td className="nm">{s.field}</td>
                          <td className="num">{s.from}</td>
                          <td className="num hot">{s.to}</td>
                          <td className="dim">
                            {s.basis}
                            <Cite className="cite">{s.ruleCitation}</Cite>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {rerate.steps.every((s) => !s.changed) && (
                  <p className="dim">Nothing moved. He comes back on exactly the card he ran.</p>
                )}
              </>
            ) : demotionOf(driverId, career) ? (
              <p className="warn">{demotionOf(driverId, career)}</p>
            ) : (
              <p className="dim">Not re-rated this winter.</p>
            )}

          </section>

          {/* ---- card history ------------------------------------------- */}
          {seasons.length > 1 && (
            <section className="dbox dbox--wide">
              <h3>Card history</h3>
              <table className="dtable">
                <thead>
                  <tr>
                    <th>Season</th>
                    <th className="num">Gas</th>
                    <th className="num">Brake</th>
                    <th className="num">Qualities</th>
                    <th>Trophy</th>
                    <th className="num">Escape</th>
                    <th className="num">Fast pit</th>
                    <th>Crew</th>
                  </tr>
                </thead>
                <tbody>
                  {seasons.map((s) => (
                    <tr key={s.season}>
                      <td className="nm">{s.season}</td>
                      <td className="num">{s.card.gasPedal}</td>
                      <td className="num">{s.card.brakePedal}</td>
                      <td className="num">{s.card.qualities.length}</td>
                      <td className="dim">{s.card.trophy ?? '—'}</td>
                      <td className="num">{s.card.escape}</td>
                      <td className="num">{s.card.fastPit}</td>
                      <td className="dim">{s.card.pitCrewGrade}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
            </>
          )}

        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UI-008 — the section that only a live race can fill
// ---------------------------------------------------------------------------

/**
 * THIS RACE, SO FAR.
 *
 * Everything here comes off `foldRace`, which reads `turns.slice(0, cursor)` —
 * so this box is a view of the same moment the boards are showing and cannot be
 * anything else. Rewind to turn 12 with the modal open and every number in it
 * goes back to turn 12.
 *
 * The chips are broken into the four piles the rules actually create, because
 * "2 chips" is not a fact — a driver in the BOTTOM group holding a suspended
 * trait chip and a driver on the lead lap holding two spendable ones are in
 * completely different positions, and a single number tells you which one you
 * are looking at exactly never.
 */
function LiveRace({ fold, raceName }: { fold: DriverRaceFold; raceName: string }) {
  const ct = useCiteText();
  const { chips } = fold;
  const started = fold.cursor > 0;

  return (
    <section className="dbox dbox--wide dbox--live">
      <h3>
        This race{started ? <> · through turn {fold.turnShowing - 1 || fold.cursor}</> : ' · on the grid'}
      </h3>
      <p className="dim small">
        {raceName}
        {started ? '' : ' — nothing has happened yet. These are the cards as they line up.'}
      </p>

      <dl className="stats">
        <Stat k="Started" v={fold.startPosition ? ordinal(fold.startPosition) : '—'} />
        <Stat
          k="Running"
          v={fold.status === 'out' ? 'OUT' : fold.position ? ordinal(fold.position) : '—'}
          bad={fold.status === 'out'}
        />
        <Stat
          k="Made up"
          v={fold.gained === null ? '—' : fold.gained === 0 ? 'level' : fold.gained > 0 ? `+${fold.gained}` : `${fold.gained}`}
          good={(fold.gained ?? 0) > 0}
          bad={(fold.gained ?? 0) < 0}
        />
        <Stat k="Best run" v={fold.bestPosition ? ordinal(fold.bestPosition) : '—'} />
        <Stat k="Worst run" v={fold.worstPosition ? ordinal(fold.worstPosition) : '—'} />
        <Stat k="Turns led" v={fold.turnsLed} hot={fold.turnsLed > 0} />
      </dl>

      {fold.status === 'out' && (
        <p className="warn small">
          Out of the race on turn {fold.outTurn ?? '?'}
          {fold.outReason ? ` — ${fold.outReason.replace('-', ' ')}` : ''}.
        </p>
      )}
      {fold.lapsDown && fold.status !== 'out' && (
        <p className="warn small">A lap down — off the lead lap, and no chip may be spent from there.</p>
      )}

      {/* ---- the chips, in the piles the rules create --------------------- */}
      <h4>Chips in hand</h4>
      <dl className="stats">
        <Stat k="Active" v={chips.active} hot={chips.active > 0} note="spendable now" />
        <Stat k="Of those, from a trait" v={chips.trait} />
        <Stat k="Reserve" v={chips.reserve} note="no value until activated" />
        <Stat k="Held, unspendable" v={chips.held} bad={chips.held > 0} />
        <Stat k="Total" v={chips.total} />
      </dl>
      <p className="dim small">
        Two active at most
        <Cite>
          {' '}— EZRA-S2: <em>"Drivers may have a maximum of two (2) Performance Chips available on any
          single game turn."</em>
        </Cite>
        . Anything past that sits in reserve and is activated at the top of a turn once the active
        pile is empty<Cite> (D-EZ-28)</Cite>.
        {chips.held > 0 && (
          <>
            {' '}The <strong>{chips.held} held</strong> {chips.held === 1 ? 'chip is' : 'chips are'} a
            trait award parked because of where he is running — <em>"The Performance Chip is not
            lost"</em>
            <Cite> (EZRA-S2 §4.0, D-EZ-26)</Cite>. It comes back the moment he climbs out.
          </>
        )}
      </p>

      {/* ---- how he has driven it ---------------------------------------- */}
      <h4>How he has driven it</h4>
      <dl className="stats">
        <Stat k="Trouble faced" v={fold.troubleAttempts} />
        <Stat k="Escaped" v={fold.troubleEscapes} good={fold.troubleEscapes > 0} />
        <Stat k="Bought out with a chip" v={fold.troubleChipSaves} />
        <Stat k="Collected" v={fold.troubleCollected} bad={fold.troubleCollected > 0} />
        <Stat
          k="Escape rate"
          v={fold.escapePct === null ? 'not asked yet' : `${Math.round(fold.escapePct * 100)}%`}
        />
        <Stat k="Duels started" v={fold.duelsInstigated} />
        <Stat k="Duels won" v={fold.duelsWon} />
        <Stat k="Duels lost" v={fold.duelsLost} />
        <Stat k="Duelled by others" v={fold.duelsDefended} />
        <Stat k="Challenges made" v={fold.challengesMade} />
        <Stat k="Challenges won" v={fold.challengesWon} />
        <Stat k="Voided on the dice" v={fold.challengesVoided} note="never reached a defender" />
        <Stat k="Challenges faced" v={fold.challengesDefended} />
        <Stat k="Held them off" v={fold.challengeDefencesHeld} />
      </dl>
      {fold.challengesVoided > 0 && (
        <p className="dim small">
          A voided call is not a lost one. Triples<Cite> (RB/vi)</Cite> and a triangle car's red 6
          <Cite> (RB/xvi)</Cite> kill
          the challenge before it reaches anybody, so those {fold.challengesVoided} are counted apart
          from the {fold.challengesMade - fold.challengesVoided} he actually contested.
        </p>
      )}

      {/* ---- chips that moved -------------------------------------------- */}
      {fold.chipMovements.length > 0 && (
        <>
          <h4>
            Chips that moved — {fold.chipsEarned} earned, {fold.chipsSpent} spent or forfeited
          </h4>
          <ul className="dweeks">
            {fold.chipMovements.map((m, i) => (
              <li key={i}>
                <span className="dweeks__r">T{m.turn}</span>
                <span className="dweeks__t">
                  {m.delta > 0 ? `+${m.delta}` : m.delta} — {ct(m.reason)}
                </span>
              </li>
            ))}
          </ul>
          <p className="dim small">
            These are movements, not a balance. The cap of two pushes a gain into reserve, the BOTTOM
            group forfeits ordinary chips outright<Cite> (RB/xv)</Cite>, and reserve chips are destroyed by losing
            track position — so the rows below will not add up to the hand above, and they are not
            meant to.
          </p>
        </>
      )}

      {/* ---- every ruling that named him --------------------------------- */}
      <h4>Every ruling that named him — {fold.incidents.length}</h4>
      {fold.incidents.length === 0 ? (
        <p className="dim">
          {started
            ? 'Nothing has happened to him yet. He has run the turns clean and unremarked.'
            : 'The green has not dropped.'}
        </p>
      ) : (
        <ul className="dincidents">
          {fold.incidents.map((inc, i) => (
            <li key={i}>
              <span className="dincidents__t">T{inc.turn}</span>
              <span className="dincidents__b">
                <span className="dincidents__d">{inc.description}</span>
                <Cite className="cite">{inc.ruleCitation}</Cite>
              </span>
              {inc.from !== undefined && (
                <span className="dincidents__m">
                  {inc.to === null
                    ? `${ordinal(inc.from)} → out`
                    : inc.to === inc.from
                      ? `held ${ordinal(inc.from)}`
                      : `${ordinal(inc.from)} → ${ordinal(inc.to as number)}`}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * BEFORE THE GREEN.
 *
 * The starting-grid screen has no tape to fold — the race has not been run yet.
 * What it has is the week that set the grid, and everything the week decided
 * about this driver travels into the race with him (RWEG/p.2-4): where he
 * starts, the chips he already holds, the temporary TV quality, who he is
 * feuding with and whether he is fuming.
 *
 * So this is not a stripped-down version of the live box. It is the other half
 * of the same fact — what he brings to the green — and it says so.
 */
function PreRace({
  row,
  standing,
  raceName,
  nameOf,
  onSelectDriver,
}: {
  row?: GridRow;
  standing: 'grid' | 'dnq' | 'bubble';
  raceName?: string;
  nameOf: (id: string) => string;
  onSelectDriver?: (driverId: string) => void;
}) {
  const ct = useCiteText();
  if (standing !== 'grid' || !row) {
    return (
      <section className="dbox dbox--wide dbox--live">
        <h3>This week · he did not make the race</h3>
        <p className="warn">
          {standing === 'dnq'
            ? 'Failed to qualify. He was at the track and he is not in the field.'
            : ct('Left on the bubble — the field rounded out to 43 without him (RWEG/p.4).')}
        </p>
        <p className="dim small">
          Going home is a real result with a real cost, not a rounding error. It shows up in his
          season as a DNQ and it changes which table re-rates his trophy in the winter<Cite> (N-010)</Cite>.
        </p>
      </section>
    );
  }

  return (
    <section className="dbox dbox--wide dbox--live">
      <h3>This week · what he brings to the green</h3>
      {raceName && <p className="dim small">{raceName}</p>}
      <dl className="stats">
        <Stat k="Starts" v={ordinal(row.position)} hot={row.position === 1} />
        <Stat k="Chips already in hand" v={row.chips} hot={row.chips > 0} />
        <Stat k="On the TV list" v={row.onTv ? (row.carriedOverTv ? 'yes · carried over' : 'yes') : 'no'} />
        <Stat k="Fuming" v={row.fuming ? 'yes' : 'no'} bad={row.fuming} />
        <Stat k="Placement" v={row.placement ?? '—'} />
      </dl>
      {row.position === 1 && <p className="dim small">On the pole.</p>}
      {row.feudWith.length > 0 && (
        <>
          <h4>Feuding with</h4>
          <ul className="dlist">
            {row.feudWith.map((id) => (
              <li key={id}>
                <button type="button" className="linkish" onClick={() => onSelectDriver?.(id)}>
                  {nameOf(id)}
                </button>
                <span className="dim">
                  {' '}— live for this race<Cite> (RB/x)</Cite>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="dim small">
        Everything on this list travels into the race with him. The chips are spendable from the
        first green turn, and the TV quality counts as held for this race only.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------

function Chip({ label, value, tone }: { label: string; value: string | number; tone?: string }) {
  return (
    <span className={`dchip${tone ? ` dchip--${tone}` : ''}`}>
      <b>{value}</b>
      {label}
    </span>
  );
}

function Stat({
  k,
  v,
  hot,
  good,
  bad,
  note,
}: {
  k: string;
  v: string | number;
  hot?: boolean;
  good?: boolean;
  bad?: boolean;
  note?: string;
}) {
  return (
    <div className="stat">
      <dt>
        {k}
        {note && <span className="stat__note">{note}</span>}
      </dt>
      <dd className={hot ? 'is-hot' : good ? 'is-good' : bad ? 'is-bad' : undefined}>{v}</dd>
    </div>
  );
}

/**
 * His card, from the race itself.
 *
 * Searched in the state the cursor points at first and the starting grid
 * second, so a driver who has already retired still has a card to show — the
 * dossier of a man who went out on turn 9 is exactly when you want to open it.
 */
function cardInRace(race: RaceDossierSource, driverId: string): DriverCard | undefined {
  const played = race.turns.slice(0, Math.max(0, Math.min(race.cursor, race.turns.length)));
  const states = [
    ...(played.length > 0 ? [played[played.length - 1].state] : []),
    race.start,
    ...(race.grid ? [race.grid] : []),
  ];
  for (const state of states) {
    const car =
      state.grid.find((c) => c.card.driverId === driverId) ??
      state.retired.find((c) => c.card.driverId === driverId);
    if (car) return car.card;
  }
  return undefined;
}

/**
 * A DRIVER'S SUNDAYS, AS A STATEMENT — D-EZ-82.
 *
 * One row per weekend: what he won, and what the career reads after it. The
 * running total is the column that turns a list of figures into a story — a dry
 * spell is a flat line and a hot streak is a climb, without a chart having to
 * draw it.
 *
 * ⚠ WON, NOT NET. `netMb` is everything that moved on his book that weekend,
 * salary and fines included, and it is deliberately NOT the headline column:
 * "he won 4 Mb at Daytona" is a fact about racing, and "he took home 12.9 Mb at
 * Daytona" is a fact about his contract. The first is what a driver card is
 * for. The second is on the row, quieter, because it is also true.
 */
export function Weekends({ races, showSeason }: { races: readonly RaceMoney[]; showSeason: boolean }) {
  if (races.length === 0) {
    return (
      <p className="dim small dmoney__none">
        No weekend has paid him yet.
      </p>
    );
  }
  return (
    <div className="dmoney">
      <div className="dmoney__head">
        <span>{showSeason ? 'Season · race' : 'Race'}</span>
        <span>Where</span>
        <span className="num">Won</span>
        <span className="num">Took home</span>
        <span className="num">Career after</span>
      </div>
      <ol className="dmoney__rows">
        {races.map((r) => (
          <li key={`${r.seasonNumber}-${r.raceNumber}`} className="dmoney__row">
            <span className="dmoney__n">
              {showSeason ? `S${r.seasonNumber} · ${r.raceNumber}` : r.raceNumber}
            </span>
            <span className="dmoney__where">{r.raceName ?? '—'}</span>
            <span className={`num${r.wonMb > 0 ? ' dmoney__won' : ' dim'}`}>
              {r.wonMb > 0 ? formatDollars(r.wonMb) : '—'}
            </span>
            <span className={`num${r.netMb < 0 ? ' dmoney__down' : ' dim'}`}>
              {r.netMb < 0 ? `−${formatDollars(-r.netMb)}` : formatDollars(r.netMb)}
            </span>
            <span className="num dmoney__run">{formatDollars(r.runningMb)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function demotionOf(driverId: string, career: CareerSeason): string | null {
  return career.offseason.demotions.find((d) => d.driverId === driverId)?.description ?? null;
}

interface Gathered {
  card: DriverCard;
  stat: DriverSeasonStats | undefined;
  team: { teamId: string; name: string; baseInk?: string } | undefined;
  money: {
    careerWinningsMb: number;
    salary: number;
    royalties: number;
    bonus: number;
    fines: number;
    balanceMb: number;
    teamBalanceMb: number;
    teamFailed: string | null;
  };
  seasons: { season: number; card: DriverCard }[];
  rerate: CareerSeason['offseason']['rerates'][number] | undefined;
  weeks: { race: number; text: string; chart: string; code: string }[];
  feuds: { other: string; otherId: string; races: number; from: number; endedBy: string | null }[];
  teammates: { driverId: string; name: string; rank: number | null }[];
  /** Which seat in the shop he holds — the liveries are varied by it. */
  teammateIndex: number;
}

/**
 * Everything about one driver, pulled from the streams that already exist.
 * Nothing here is stored; every number is read at the moment it is shown, which
 * is the same discipline the database keeps.
 */
function gather(driverId: string, career: CareerSeason, history: CareerSeason[]): Gathered | null {
  const card =
    career.offseason.roster.find((c) => c.driverId === driverId) ??
    career.roster.find((c) => c.driverId === driverId) ??
    career.season.finalRoster.find((c) => c.driverId === driverId);
  if (!card) {
    // A driver who left the league before this season — retired, lost his
    // licence — still has a dossier: his LAST season's, read off that season
    // and the ones before it (D-EZ-95's registry, audit 4 Oct 2026).
    const last = [...history].reverse().find(
      (s) => s.roster.some((c) => c.driverId === driverId) || s.season.finalRoster.some((c) => c.driverId === driverId),
    );
    if (!last) return null;
    return gather(driverId, last, history.filter((s) => s.seasonNumber < last.seasonNumber));
  }

  const stat = career.season.finalStandings.byDriver[driverId];
  const teamRow = career.teams.find((t) => t.driverIds.includes(driverId));
  const financeTeamId = career.teamOf[driverId];

  const book = career.ledger.books[`driver:${driverId}`];
  const teamBook = financeTeamId ? career.ledger.books[`team:${financeTeamId}`] : undefined;
  const failure = career.offseason.teamFailures.find((f) => f.driverIds.includes(driverId));

  // Card history across every season played, plus this winter's new card.
  const played = [...history, career];
  const seasons: { season: number; card: DriverCard }[] = [];
  for (const s of played) {
    const c = s.roster.find((x) => x.driverId === driverId);
    if (c) seasons.push({ season: s.seasonNumber, card: c });
  }
  const nextCard = career.offseason.roster.find((c) => c.driverId === driverId);
  if (nextCard && seasons.length > 0) seasons.push({ season: career.seasonNumber + 1, card: nextCard });

  const weeks: Gathered['weeks'] = [];
  for (const race of career.season.races) {
    const reading = race.week.tvResults.find((r) => r.driverId === driverId);
    if (reading) weeks.push({ race: race.raceNumber, text: reading.text, chart: reading.chart, code: reading.code });
  }

  const nameOf = (id: string): string =>
    career.season.finalStandings.byDriver[id]?.name ??
    career.roster.find((c) => c.driverId === id)?.name ??
    id;

  const feuds = career.season.feudThreads
    .filter((f) => f.a === driverId || f.b === driverId)
    .map((f) => {
      const otherId = f.a === driverId ? f.b : f.a;
      return { other: nameOf(otherId), otherId, races: f.races.length, from: f.startedRace, endedBy: f.endedBy };
    })
    .sort((a, b) => b.races - a.races);

  const teammates = (teamRow?.driverIds ?? [])
    .filter((id) => id !== driverId)
    .map((id) => ({
      driverId: id,
      name: nameOf(id),
      rank: career.season.finalStandings.byDriver[id]?.rank ?? null,
    }));

  return {
    card,
    stat,
    team: teamRow ? { teamId: teamRow.teamId, name: teamRow.name, baseInk: teamRow.baseInk } : undefined,
    money: {
      careerWinningsMb: book?.careerWinningsMb ?? 0,
      salary: book?.byCategory.salary ?? 0,
      royalties: book?.byCategory.royalties ?? 0,
      bonus: book?.byCategory.bonus ?? 0,
      fines: -(book?.byCategory.fine ?? 0),
      balanceMb: book?.balanceMb ?? 0,
      teamBalanceMb: teamBook?.balanceMb ?? 0,
      teamFailed: failure?.description ?? null,
    },
    seasons,
    rerate: career.offseason.rerates.find((r) => r.driverId === driverId),
    weeks,
    feuds,
    teammates,
    teammateIndex: teamRow ? teamRow.driverIds.indexOf(driverId) : -1,
  };
}

/** A rate as a whole percent, or an em dash when the denominator was zero. */
const pct = (v: number | null): string => (v === null ? '—' : `${Math.round(v)}%`);
